import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getActiveSubscription } from "../../redux/plans/plansAction";
import Modal from "../ui/Modal/Modal";
import { AlertCircle, CreditCard, ArrowRight } from "lucide-react";
import { clsx } from "clsx";
import styles from "./SubscriptionGuard.module.scss";

/**
 * SubscriptionGuard - Protects features based on active subscription status
 * If no active subscription is found, it shows a blocking message
 */
const SubscriptionGuard = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeSubscription, isLoading, isLoaded } = useSelector((state) => state.plans);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [showBlockingModal, setShowBlockingModal] = useState(false);

  // We allow access to these paths even without a subscription
  const allowedPaths = ["/app/subscription", "/app/profile", "/app/settings"];
  const isAllowedPath = allowedPaths.some(path => location.pathname.startsWith(path));

  useEffect(() => {
    if (isAuthenticated && !isLoaded && !isLoading) {
      dispatch(getActiveSubscription());
    }
  }, [dispatch, isAuthenticated, isLoaded, isLoading]);

  // Handling both array and object formats from API
  const sub = Array.isArray(activeSubscription) ? activeSubscription[0] : activeSubscription;
  
  const isExpiredByDate = sub?.endDate && new Date(sub.endDate) < new Date();
  
  const hasNoPlan = isLoaded && !sub;
  const isExpired = sub && (
                      (sub.daysRemaining !== undefined && sub.daysRemaining <= 0) ||
                      (sub.status === "Expired") ||
                      isExpiredByDate
                    );

  const isInactive = hasNoPlan || isExpired;

  useEffect(() => {
    if (isAuthenticated && isLoaded && !isLoading && isInactive && !isAllowedPath) {
      setShowBlockingModal(true);
    } else {
      setShowBlockingModal(false);
    }
  }, [isInactive, isLoading, isLoaded, isAuthenticated, isAllowedPath]);

  const handleRenew = () => {
    setShowBlockingModal(false);
    navigate("/pricing");
  };

  const handleGoToSubscription = () => {
    setShowBlockingModal(false);
    navigate("/app/subscription");
  };

  return (
    <>
      {children}

      <Modal
        isOpen={showBlockingModal}
        onClose={() => {}} // Non-closable
        title={hasNoPlan ? "Subscription Required" : "Subscription Status"}
        size="md"
        showCloseButton={false}
        closeOnOutsideClick={false}
      >
        <div className={styles.blockingContent}>
          <div className={styles.iconWrapper}>
            <AlertCircle size={48} className={styles.alertIcon} />
          </div>
          <h2>{hasNoPlan ? "Welcome to BizAura" : "Plan Expired"}</h2>
          <p>
            {hasNoPlan 
              ? "To get started, please select a subscription plan that fits your business needs."
              : "Your subscription has ended. Please renew to continue using all features."
            }
          </p>
          
          <div className={styles.planDetails}>
            <div className={styles.planCard}>
              <CreditCard size={24} />
              <div>
                <h4>{hasNoPlan ? "Plan Selection Required" : "Renewal Required"}</h4>
                <p>
                  {hasNoPlan 
                    ? "Choose from our flexible plans to unlock your dashboard."
                    : "Access to dashboard features is currently restricted."
                  }
                </p>
              </div>
            </div>
          </div>

          <div className={styles.actions}>
            <button className={styles.renewBtn} onClick={handleRenew}>
              {hasNoPlan ? "Choose a Plan" : "Renew Now"}
              <ArrowRight size={18} />
            </button>
            <button className={styles.secondaryBtn} onClick={handleGoToSubscription}>
              View Details
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SubscriptionGuard;
